import { useState, useEffect } from 'react';
import api from '@/api';
import { formatLabel, ACTION_LABELS, formatFieldName, timeAgo, BADGE } from '@/lib/format';
import { FileText, PencilSimple, Trash, ArrowsClockwise, MagnifyingGlass, ShieldCheck, Bell, Key, SignOut, UserPlus, CheckCircle, Scales, CloudArrowDown, GearSix, Play, Stop, Plug, ListBullets, Plus, Minus } from '@phosphor-icons/react';

const ICON_MAP = { FileText, PencilSimple, Trash, ArrowsClockwise, MagnifyingGlass, ShieldCheck, Bell, Key, SignOut, UserPlus, CheckCircle, Scales, CloudArrowDown, GearSix, Play, Stop, Plug, ListBullets, Plus, Minus };

// 6C: Category tabs
const CATEGORIES = [
  { id: 'all', label: 'All' },
  { id: 'cases', label: 'Cases', filter: { entity_type: 'case' } },
  { id: 'screening', label: 'Screening', filter: { entity_type: 'screening' } },
  { id: 'monitoring', label: 'Monitoring', filter: { entity_type: 'monitoring' } },
  { id: 'data', label: 'Data Sources', filter: { entity_type: 'data_source' } },
  { id: 'users', label: 'Users', filter: { entity_type: 'user' } },
];

export default function AuditPage() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [category, setCategory] = useState('all');
  const [actionFilter, setActionFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [loading, setLoading] = useState(true);
  useEffect(() => { document.title = "Radar — Audit Trail"; }, []);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = {};
      const cat = CATEGORIES.find(c => c.id === category);
      if (cat?.filter) Object.assign(params, cat.filter);
      if (actionFilter) params.action = actionFilter;
      if (userFilter) params.user_email = userFilter;
      const [l, s] = await Promise.all([api.get('/audit', { params }), api.get('/audit/stats')]);
      setLogs(l.data.logs);
      setTotal(l.data.total);
      setStats(s.data);
    } catch (e) { }
    setLoading(false);
  };

  useEffect(() => { fetchLogs(); }, [category, actionFilter, userFilter]);

  const getActionInfo = (action) => {
    const info = ACTION_LABELS[action];
    if (info) return info;
    return { label: formatLabel(action), icon: 'FileText' };
  };

  return (
    <div data-testid="audit-page">
      <h1 className="font-heading text-2xl font-bold text-[#0A0A0A] mb-1">Audit Trail</h1>
      <p className="text-sm text-[#52525B] mb-6">Detailed history of all actions performed in the system</p>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <div className="bg-white border border-[#E4E4E7] rounded-md p-4">
            <p className="text-xs uppercase tracking-wider text-[#52525B] font-medium">Total Entries</p>
            <p className="text-2xl font-bold">{stats.total_entries}</p>
          </div>
          <div className="bg-white border border-[#E4E4E7] rounded-md p-4">
            <p className="text-xs uppercase tracking-wider text-[#52525B] font-medium">Unique Actions</p>
            <p className="text-2xl font-bold">{stats.actions?.length || 0}</p>
          </div>
          <div className="bg-white border border-[#E4E4E7] rounded-md p-4">
            <p className="text-xs uppercase tracking-wider text-[#52525B] font-medium">Unique Users</p>
            <p className="text-2xl font-bold">{stats.unique_users}</p>
          </div>
          <div className="bg-white border border-[#E4E4E7] rounded-md p-4">
            <p className="text-xs uppercase tracking-wider text-[#52525B] font-medium">Most Common</p>
            <p className="text-sm font-medium mt-1 truncate">{stats.action_counts ? getActionInfo(Object.entries(stats.action_counts).sort((a, b) => b[1] - a[1])?.[0]?.[0]).label : '-'}</p>
          </div>
        </div>
      )}

      {/* 6C: Category Tabs */}
      <div className="flex gap-1 mb-4 border-b border-[#E4E4E7]">
        {CATEGORIES.map(c => (
          <button key={c.id} onClick={() => setCategory(c.id)} className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-all ${category === c.id ? 'border-[#0052FF] text-[#0052FF]' : 'border-transparent text-[#52525B] hover:text-[#0A0A0A]'}`}>{c.label}</button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex gap-3 mb-4">
        <select value={actionFilter} onChange={e => setActionFilter(e.target.value)} className="px-3 py-2 text-sm border border-[#E4E4E7] rounded-md">
          <option value="">All Actions</option>
          {stats?.actions?.map(a => <option key={a} value={a}>{getActionInfo(a).label}</option>)}
        </select>
        <input value={userFilter} onChange={e => setUserFilter(e.target.value)} placeholder="Filter by user email..." className="px-3 py-2 text-sm border border-[#E4E4E7] rounded-md flex-1 max-w-xs" />
      </div>

      {/* Logs */}
      {loading ? (
        <div className="flex justify-center py-12"><div className="w-8 h-8 border-2 border-[#0052FF] border-t-transparent rounded-full animate-spin" /></div>
      ) : (
        <div className="bg-white border border-[#E4E4E7] rounded-md overflow-hidden">
          {logs.length === 0 ? (
            <div className="p-8 text-center text-sm text-[#A1A1AA]">No audit logs found for this category.</div>
          ) : (
            <div>
              {logs.map((l, i) => {
                const info = getActionInfo(l.action);
                const IconComp = ICON_MAP[info.icon] || FileText;
                return (
                  <div key={i} className="px-4 py-3 border-b border-[#E4E4E7] last:border-0 hover:bg-[#F7F7F8] transition-all">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <IconComp size={16} weight="duotone" className="text-[#52525B] shrink-0" />
                        <span className="text-sm font-medium text-[#0A0A0A]">{info.label}</span>
                        <span className="text-xs text-[#A1A1AA]">{formatLabel(l.entity_type)}{l.entity_id ? ` #${l.entity_id.slice(0, 8)}` : ''}</span>
                        <span className="text-xs text-[#52525B]">by <strong>{l.user_email}</strong></span>
                      </div>
                      <span className="text-xs text-[#A1A1AA] shrink-0 ml-4">{timeAgo(l.timestamp)}</span>
                    </div>
                    {/* 6B: Human-readable before/after diff table */}
                    {(l.before || l.after) && (
                      <div className="mt-2 ml-7">
                        <table className="text-xs">
                          <thead><tr className="border-b border-[#E4E4E7]">
                            <th className="text-left py-1 pr-4 text-[#52525B] font-medium">Field</th>
                            <th className="text-left py-1 pr-4 text-red-600 font-medium">Before</th>
                            <th className="text-left py-1 text-green-600 font-medium">After</th>
                          </tr></thead>
                          <tbody>
                            {Object.keys({ ...(l.before || {}), ...(l.after || {}) }).map(k => (
                              <tr key={k} className="border-b border-[#F0F0F1]">
                                <td className="py-1 pr-4 text-[#52525B]">{formatFieldName(k)}</td>
                                <td className="py-1 pr-4 text-red-600">{l.before?.[k] != null ? formatLabel(String(l.before[k])) : '—'}</td>
                                <td className="py-1 text-green-600">{l.after?.[k] != null ? formatLabel(String(l.after[k])) : '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                    {l.details && Object.keys(l.details).length > 0 && !l.before && !l.after && (
                      <p className="text-xs text-[#A1A1AA] mt-1 ml-7">{Object.entries(l.details).map(([k, v]) => `${formatFieldName(k)}: ${v}`).join(' • ')}</p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          <div className="px-4 py-2 border-t border-[#E4E4E7] text-xs text-[#A1A1AA]">{total} total entries</div>
        </div>
      )}
    </div>
  );
}
